import { FC, useState, useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import { PlusCircle, Plus, Users } from '@phosphor-icons/react';
import type { CreateAccountResponse } from 'api/account/handlers/createAccountHandler';
import type { CreateAccountRequest } from 'common/account/CreateAccountDocuments';
import Api from 'client/lib/Api';
import { useError } from 'client/lib/Error';
import { useUser } from 'client/lib/User';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Dialog } from 'client/components/Dialog/Dialog';
import { Input } from 'client/components/Input';

const inputStyle = css`
  margin-bottom: 16px;
`;

const createAccount = (request: CreateAccountRequest) => Api.post<CreateAccountRequest, CreateAccountResponse>('/account/create', request);

export interface CreateAccountDialogProps {
  open: boolean;
  onCreate: (accountID: number) => void;
  onClose: () => void;
}

export const CreateAccountDialog: FC<CreateAccountDialogProps> = ({ open, onCreate, onClose }) => {
  const theme = useTheme();
  const { setError } = useError({ showSnackbar: true });
  const { updateSession } = useUser();
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');

    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 0);
  }, [open]);

  const handleCreate = async () => {
    setError(null);
    setIsLoading(true);

    const response = await createAccount({ name });
    if (response.status !== 'ok') {
      setIsLoading(false);
      return setError('Something went wrong while creating the account. Please try again…', { status: (response as any).status });
    }

    await updateSession();

    setIsLoading(false);

    onCreate(response.account.id);
  };

  return (
    <Dialog
      open={open}
      title="Create an Account"
      description="Create a new account and add yourself as an administrator to it"
      icon={<Users color={theme.color.primary[100]} />}
      iconOrnament={<PlusCircle color={theme.color.primary[100]} />}
      actions={
        <>
          <Button primary icon={<Plus weight="bold" />} loading={isLoading} onClick={handleCreate}>
            Create Account
          </Button>
        </>
      }
      onClose={onClose}
    >
      {/* <div css={helpStyle}>
        <Button href="https://www.ssh.com/academy/ssh-keys" external small icon={<ArrowSquareOut />}>
          What is an SSH key?
        </Button>
        <Button
          href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh/checking-for-existing-ssh-keys"
          external
          small
          icon={<ArrowSquareOut />}
        >
          How to check for existing keys?
        </Button>
        <Button
          href="https://docs.github.com/en/authentication/connecting-to-github-with-ssh/generating-a-new-ssh-key-and-adding-it-to-the-ssh-agent"
          external
          small
          icon={<ArrowSquareOut />}
        >
          How to create an SSH key?
        </Button>
        <Button href="https://stackoverflow.com/a/3828169" external small icon={<ArrowSquareOut />}>
          How to find the public key?
        </Button>
      </div> */}

      <Input
        label="Name"
        value={name}
        large
        onChange={(event) => setName(event.currentTarget.value)}
        ref={(input) => {
          nameInputRef.current = input;
        }}
        css={inputStyle}
      />

      <Banner>
        You will be automatically added to this account as an administrator. You can invite other users after the account has been created.
      </Banner>
    </Dialog>
  );
};
